import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type ZodType } from 'zod';
import { Icon } from '~/atoms/Icon';
import { Reveal } from '~/atoms/Reveal';
import { Alert, Select } from '~/molecules';
import { HeadingRow } from '~/molecules/HeadingRow.tsx';

import { type ResolvedDataImportFile } from '../dataImport.config.ts';
import {
  type DataImportCopyOverrides,
  useDataImportCopy,
} from '../dataImport.copy.ts';

type P = {
  readonly allHeaderFields?: string[];
  /** The wizard's already-resolved copy; standalone use falls back to the defaults. */
  readonly copy?: DataImportCopyOverrides;
  readonly fileConfig: ResolvedDataImportFile;
  readonly headerFields: string[];
  readonly headerValidationRules?: ZodType;
  readonly initialColumnMapping?: Record<string, string>;
  readonly mandatoryHeaderFields?: string[];
  readonly onColumnMappingChange: ({
    columnMapping,
    isValid,
  }: {
    columnMapping: Record<string, string>;
    isValid: boolean;
  }) => void;
};

export const ColumnMappingForm = ({
  copy: copyOverrides,
  fileConfig,
  headerFields,
  initialColumnMapping,
  onColumnMappingChange,
}: P) => {
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(
    {},
  );

  const {
    allHeaderFields,
    fieldLabels,
    headerValidationRules,
    mandatoryHeaderFields,
    defaultColumnMapping,
  } = fileConfig;
  const optionalHeaderFields = allHeaderFields?.filter(
    (header) => !mandatoryHeaderFields?.includes(header),
  );

  const copy = useDataImportCopy(copyOverrides);
  const initialColumnMappingRef = useRef(initialColumnMapping);

  useEffect(() => {
    if (!headerFields) return;

    // Step 1: Find headers that match defaultColumnMapping (translated names)
    const defaultExistingColumnMapping = Object.fromEntries(
      Object.entries(defaultColumnMapping ?? {}).filter(([, value]) =>
        headerFields?.includes(value),
      ),
    );

    // Step 2: Find headers that match basic field names (not in translations)
    const translatedHeaderValues = Object.values(defaultColumnMapping ?? {});
    const directFieldMapping = Object.fromEntries(
      headerFields
        ?.filter(
          (field) =>
            allHeaderFields?.includes(field) && // Must be a valid field name
            !translatedHeaderValues.includes(field), // Must not already be handled by translations
        )
        .map((field) => [field, field]),
    );

    // Step 3: Combine both approaches for mixed scenario support
    const initialColumnMapping = {
      ...defaultExistingColumnMapping,
      ...directFieldMapping,
    };
    const mapping = initialColumnMappingRef.current ?? initialColumnMapping;

    setColumnMapping(mapping);
    onColumnMappingChange({
      columnMapping: mapping,
      isValid: false,
    });
  }, [
    allHeaderFields,
    headerFields,
    defaultColumnMapping,
    onColumnMappingChange,
  ]);

  const getFilteredOptions = useCallback(
    (currentHeader: string) => {
      return headerFields
        ?.filter(
          (field) =>
            !Object.values(columnMapping).includes(field) ||
            columnMapping[currentHeader] === field,
        )
        .map((field) => ({
          label: field,
          value: field,
        }));
    },
    [headerFields, columnMapping],
  );

  const parsingError = useMemo(() => {
    return headerValidationRules?.safeParse(Object.keys(columnMapping));
  }, [columnMapping, headerValidationRules]);

  const fatalErrors = useMemo(() => {
    const errors = parsingError?.error?.issues;

    if (!errors) return [];

    const returnedErrors = Object.fromEntries(
      errors
        .filter((error) => error.params?.errorCode === 'mandatory')
        .map((error) => [
          error.params?.header ?? error.path?.[0] ?? 'unknown',
          error.message,
        ]),
    );

    return returnedErrors;
  }, [parsingError]);

  const warnings = useMemo(() => {
    const warningList: Array<{ message: string; details?: string[] }> = [];

    // Warning for unmapped columns from the file
    if (headerFields) {
      const mappedColumns = Object.values(columnMapping).filter(Boolean);
      const unmappedColumns = headerFields.filter(
        (column) => !mappedColumns.includes(column),
      );

      if (unmappedColumns.length > 0) {
        warningList.push({
          message: copy.unmappedColumnsWarning,
          details: unmappedColumns,
        });
      }
    }

    return warningList;
  }, [columnMapping, copy, headerFields]);

  // Map the existing value in the table to a proper import header
  const handleColumnMappingChange = (header: string, value: string) => {
    const newColumnMapping = { ...columnMapping, [header]: value };
    setColumnMapping(newColumnMapping);
  };

  const handleColumnMappingClear = (header: string) => {
    const newColumnMapping = { ...columnMapping };
    delete newColumnMapping[header];

    setColumnMapping(newColumnMapping);
  };

  useEffect(() => {
    onColumnMappingChange({
      columnMapping,
      isValid: Object.keys(fatalErrors).length === 0,
    });
  }, [fatalErrors, columnMapping, onColumnMappingChange]);

  const mandatoryHeaderFieldsToRender = useMemo(() => {
    if (!mandatoryHeaderFields) return [];
    const returnFields = [...mandatoryHeaderFields];
    for (const error of Object.keys(fatalErrors)) {
      if (!returnFields?.includes(error)) {
        returnFields?.push(error);
      }
    }

    return returnFields;
  }, [mandatoryHeaderFields, fatalErrors]);

  const optionalHeaderFieldsToRender = useMemo(() => {
    if (!optionalHeaderFields) return [];
    const returnFields = [...optionalHeaderFields];

    for (const error of Object.keys(fatalErrors)) {
      if (returnFields?.includes(error)) {
        returnFields?.splice(returnFields.indexOf(error), 1);
      }
    }

    return returnFields;
  }, [optionalHeaderFields, fatalErrors]);

  const renderMappingRow = (header: string, isMandatory: boolean) => {
    const error = fatalErrors[header];
    const inputId = `column-mapping-${header}`;
    const value =
      Object.entries(columnMapping).find(([key]) => key === header)?.[1] ?? '';

    return (
      <Fragment key={header}>
        <label className="self-center" htmlFor={inputId}>
          {fieldLabels?.[header] ?? header}
          {isMandatory ? (
            <span className="text-tiga-semantic-danger"> *</span>
          ) : null}
        </label>
        <div className="self-center p-2">
          <Icon className="text-tiga-text-lighter" metaphor="arrowForward" />
        </div>
        <div>
          <Select
            errors={error ? [error] : undefined}
            inputId={inputId}
            options={getFilteredOptions(header)}
            placeholder={
              isMandatory
                ? copy.assignRequiredColumn
                : copy.assignOptionalColumn
            }
            value={value}
            onChange={(
              value: string,
              _data: unknown,
              actionMeta: { action: string },
            ) => {
              if (actionMeta.action === 'clear') {
                handleColumnMappingClear(header);
                return;
              }

              handleColumnMappingChange(header, value);
            }}
          />
          {isMandatory && error ? (
            <div className="text-tiga-semantic-danger">
              {copy.requiredFieldMessage}
            </div>
          ) : null}
        </div>
      </Fragment>
    );
  };

  return (
    <div className="">
      {/* Warning Section */}
      <Reveal show={warnings.length > 0}>
        <Alert className="mb-4" severity="warning">
          <div className="space-y-2">
            {warnings.map((warning, index) => (
              <div key={index}>
                {warning.message}
                {warning.details ? (
                  <details className="mt-2" open={warning.details.length <= 2}>
                    <summary className="cursor-pointer text-sm font-medium hover:text-amber-700">
                      {copy.unmappedColumnsDetails(warning.details.length)}
                    </summary>
                    <div className="mt-2 ml-4 border-l-2 border-amber-200 pl-2">
                      <div className="space-y-1 text-sm">
                        {warning.details.map((detail, detailIndex) => (
                          <div
                            key={detailIndex}
                            className="rounded bg-amber-50 px-2 py-1 font-mono text-xs"
                          >
                            {detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  </details>
                ) : null}
              </div>
            ))}
          </div>
        </Alert>
      </Reveal>

      <div className="grid w-full grid-cols-[1fr_40px_1fr] gap-x-2 gap-y-2 text-sm">
        <div className="col-span-3 grid grid-cols-subgrid border-b border-tiga-border-lighter pb-1">
          <div className="text-tiga-text-lighter">
            {copy.systemColumnsHeading}
          </div>
          <div className="text-tiga-text-lighter" />
          <div className="text-tiga-text-lighter">
            {copy.sourceColumnsHeading}
          </div>
        </div>

        <HeadingRow
          className="col-span-3 mt-2"
          heading={copy.requiredColumnsHeading}
          variant="h3"
        />
        {mandatoryHeaderFieldsToRender?.map((header) =>
          renderMappingRow(header, true),
        )}

        <HeadingRow
          className="col-span-3 mt-2"
          heading={copy.optionalColumnsHeading}
          variant="h3"
        />
        {optionalHeaderFieldsToRender?.map((header) =>
          renderMappingRow(header, false),
        )}
      </div>
    </div>
  );
};
